import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/auth.store';
import { usePermissions } from '../../hooks/usePermissions';
import { firstAllowedPage, hasPermission } from '../../lib/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

// Everything behind this needs a signed-in user
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Shown in place of a page the user's role does not allow
export const NoAccess: React.FC<{ permission?: string }> = ({ permission }) => {
  const { permissions } = usePermissions();
  const home = firstAllowedPage(permissions);
  return (
    <div className="mx-auto mt-16 max-w-md rounded-lg bg-white p-8 text-center shadow-sm ring-1 ring-gray-200">
      <LockClosedIcon className="mx-auto h-10 w-10 text-gray-300" />
      <h1 className="mt-4 text-lg font-semibold text-gray-900">You don't have access to this page</h1>
      <p className="mt-2 text-sm text-gray-500">
        Your role does not include it. If you need it, ask an administrator to change your role.
      </p>
      {permission && <p className="mt-2 text-xs text-gray-400">Needed permission: {permission}</p>}
      <Link
        to={home}
        className="mt-6 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
      >
        Go to a page you can open
      </Link>
    </div>
  );
};

// A page that needs a permission. Without it the user sees why, instead of a page full of errors.
export const RequirePermission: React.FC<{ permission: string; children: React.ReactNode }> = ({ permission, children }) => {
  const { permissions } = usePermissions();
  return hasPermission(permissions, permission) ? <>{children}</> : <NoAccess permission={permission} />;
};

// "/" opens the user's chosen start page, or the first page their role allows
export const StartRedirect: React.FC<{ preferred: string }> = ({ preferred }) => {
  const { permissions } = usePermissions();
  return <Navigate to={firstAllowedPage(permissions, preferred)} replace />;
};

export default ProtectedRoute;
